import * as React from 'react'
import type { LaunchPlcUpdateItem } from '@axilon-apps/domains-ontology-ui-state'
import { readMutationError } from '../../../components/explorer'
import {
  ADD_SOURCES,
  UPDATE_SOURCES,
  defaultModifyAction,
  defaultSub,
  emptyPrep,
  isModifyActionAvailable,
  LAUNCHABLE
} from './constants'
import {
  allPrepReady,
  buildPlanIssues,
  hasBlockingIssues,
  isOptionalPrepSkipped,
  isSourcePrepReady,
  orderedSelected
} from './plan'
import { siteOptionLabel } from './siteIds'
import {
  createIngestWizardDraft,
  getIngestWizardDraft,
  getIngestWizardDraftsSnapshot,
  loadIngestWizardDrafts,
  removeIngestWizardDraft,
  reserveIngestWizardRunName,
  stageIngestWizardSourceFiles,
  subscribeIngestWizardDrafts,
  updateIngestWizardDraft
} from './wizardPersistence'
import type {
  ModifyAction,
  ModifyResourceId,
  PrepValues,
  SourceId,
  SubOptionId,
  WizardRunState,
  WizardState
} from './types'

/** Operator intent for the Update → PSS step (`StepUpdatePss`). */
export type PssUpdateInput =
  | {
      action: 'backfill'
      mirrorId: string
      connectionName: string
      windowStart: string
      windowEnd: string
    }
  | { action: 'sync_tags'; mirrorId: string; connectionName: string }

const INITIAL: WizardState = {
  draftId: '',
  updatedAt: 0,
  step: 0,
  view: 'form',
  flow: 'new',
  runName: '',
  mode: null,
  newSiteName: '',
  existingSiteId: null,
  existingSiteName: null,
  selected: new Set(),
  sub: {},
  waived: new Set(),
  prep: {},
  optionalSources: new Set(),
  openPrep: null,
  modResource: null,
  modAction: null,
  modStage: null,
  modTargetId: null,
  modAck: false,
  launchQueue: [],
  activeLaunchId: null,
  launched: new Set(),
  queueStarted: false,
  run: {
    statuses: {},
    errors: {},
    jobIdsBySource: {},
    paused: false,
    pausedSourceId: null,
    pauseReason: null,
    editing: false
  }
}

function ensurePrep(
  prep: WizardState['prep'],
  ids: SourceId[],
  sub: WizardState['sub']
): WizardState['prep'] {
  const next = { ...prep }
  for (const id of ids) {
    if (!next[id]) next[id] = emptyPrep(id, sub[id] ?? defaultSub(id))
  }
  return next
}

export function useIngestWizard(initialDraftId: string | null = null) {
  const drafts = React.useSyncExternalStore(
    subscribeIngestWizardDrafts,
    getIngestWizardDraftsSnapshot,
    getIngestWizardDraftsSnapshot
  )
  React.useEffect(() => {
    void loadIngestWizardDrafts()
  }, [])
  const [activeDraftId, setActiveDraftId] = React.useState<string | null>(initialDraftId)
  const [runNameError, setRunNameError] = React.useState<string | null>(null)
  const [step1Saving, setStep1Saving] = React.useState(false)
  const state = activeDraftId ? (getIngestWizardDraft(activeDraftId) ?? INITIAL) : INITIAL
  const boundDraftId = state.draftId
  const setState = React.useCallback<React.Dispatch<React.SetStateAction<WizardState>>>(
    (update) => {
      if (boundDraftId) updateIngestWizardDraft(boundDraftId, update)
    },
    [boundDraftId]
  )

  const siteName =
    state.mode === 'new' ? state.newSiteName.trim() : (state.existingSiteName ?? '').trim()
  const runNameLength = Array.from(state.runName.trim()).length

  const step1Valid =
    runNameLength <= 100 &&
    (state.mode === 'new'
      ? state.newSiteName.trim().length > 0
      : state.mode === 'existing' && !!state.existingSiteId)

  const issues = React.useMemo(() => buildPlanIssues(state), [state])
  const planClean = !hasBlockingIssues(issues)
  const prepReady = allPrepReady(state)

  const startWizard = async () => {
    const created = await createIngestWizardDraft({
      ...INITIAL,
      draftId: '',
      updatedAt: Date.now(),
      step: 1,
      view: 'form',
      flow: 'new'
    })
    setActiveDraftId(created.draftId)
  }

  const startWizardWithSource = async (id: SourceId, fields: Record<string, string> = {}) => {
    const created = await createIngestWizardDraft({
      ...INITIAL,
      draftId: '',
      updatedAt: Date.now(),
      step: 1,
      view: 'form',
      flow: 'new',
      selected: new Set([id]),
      prep: {
        [id]: {
          ...emptyPrep(id, defaultSub(id)),
          fields
        }
      }
    })
    setActiveDraftId(created.draftId)
  }

  const goList = () => setActiveDraftId(null)

  const resumeDraft = (draftId?: string) => {
    const id = draftId ?? drafts[0]?.draftId
    if (id && getIngestWizardDraft(id)) setActiveDraftId(id)
  }

  const discardDraft = (draftId?: string) => {
    const id = draftId ?? boundDraftId
    if (!id) return
    removeIngestWizardDraft(id)
    if (id === activeDraftId) setActiveDraftId(null)
  }

  const getState = React.useCallback(
    () => (boundDraftId ? (getIngestWizardDraft(boundDraftId) ?? state) : state),
    [boundDraftId, state]
  )
  const hasDraft = React.useCallback(
    () => Boolean(boundDraftId && getIngestWizardDraft(boundDraftId)),
    [boundDraftId]
  )

  const setMode = (mode: 'new' | 'existing') =>
    setState((s) => ({
      ...s,
      mode,
      existingSiteId: mode === 'existing' ? s.existingSiteId : null,
      existingSiteName: mode === 'existing' ? s.existingSiteName : null
    }))

  const setNewSiteName = (newSiteName: string) => setState((s) => ({ ...s, newSiteName }))

  const setRunName = (runName: string) => {
    setRunNameError(null)
    setState((s) => ({ ...s, runName }))
  }

  const pickExistingSite = (id: string, name: string) =>
    setState((s) => ({
      ...s,
      existingSiteId: id,
      existingSiteName: siteOptionLabel(name, id)
    }))

  const goStep2 = async () => {
    if (!step1Valid || step1Saving || !boundDraftId) return
    setStep1Saving(true)
    setRunNameError(null)
    try {
      const runName = state.runName.trim()
      if (runName) {
        await reserveIngestWizardRunName(boundDraftId, runName)
      }
      if (state.launched.size > 0) {
        setState((s) => ({
          ...s,
          step: 2,
          view: 'form',
          modStage: s.flow === 'modify' ? 'pick' : null
        }))
        return
      }
      setState((s) => ({
        ...s,
        runName: s.runName.trim(),
        step: 2,
        view: 'form',
        flow: s.mode === 'existing' && !s.selected.has('tabular') ? 'modify' : 'new',
        modStage: s.mode === 'existing' && !s.selected.has('tabular') ? 'pick' : null,
        modResource: null,
        modAction: null,
        optionalSources: s.optionalSources
      }))
    } catch (error) {
      setRunNameError(readMutationError(error, 'Could not save the ingest run name.'))
    } finally {
      setStep1Saving(false)
    }
  }

  const backTo1 = () => setState((s) => ({ ...s, step: 1, view: 'form', modStage: null }))

  const toggleSource = (id: SourceId) =>
    setState((s) => {
      const selected = new Set(s.selected)
      const sub = { ...s.sub }
      if (selected.has(id)) {
        selected.delete(id)
        // Drop required children when parent unchecked
        if (id === 'plc') selected.delete('ipmap')
        if (id === 'robotics') selected.delete('roboticsmap')
        if (id === 'vision') selected.delete('visionmap')
      } else {
        selected.add(id)
        const d = defaultSub(id)
        if (d && !sub[id]) sub[id] = d
      }
      return {
        ...s,
        selected,
        sub,
        prep: ensurePrep(s.prep, [...selected], sub)
      }
    })

  const setSub = (id: SourceId, value: SubOptionId) =>
    setState((s) => {
      const prep = { ...s.prep }
      // Keep the staged file when switching Ignition/AVEVA; StepCollect
      // validates it against the active accept list so Continue stays honest.
      if (id === 'scada' && prep.scada) {
        const next = emptyPrep('scada', value)
        prep.scada = {
          ...next,
          file: prep.scada.file,
          files: prep.scada.files,
          fields: { ...next.fields, ...prep.scada.fields }
        }
      }
      if (id === 'safetymanager' && prep.safetymanager) {
        const next = emptyPrep('safetymanager', value)
        const prev = prep.safetymanager
        const keepFile = value !== '15.2'
        prep.safetymanager = {
          ...next,
          file: keepFile ? prev.file : null,
          files: keepFile ? prev.files : [],
          fields: { ...next.fields, ...prev.fields }
        }
      }
      return { ...s, sub: { ...s.sub, [id]: value }, prep }
    })

  const checkPlan = () => {
    if (state.selected.size === 0) return
    setState((s) => ({ ...s, view: 'review' }))
  }

  const backTo2 = () => setState((s) => ({ ...s, view: 'form' }))

  const waiveIssue = (id: string) =>
    setState((s) => {
      const waived = new Set(s.waived)
      waived.add(id)
      return { ...s, waived }
    })

  const fixIssue = (_issueId: string, sourceId?: SourceId) =>
    setState((s) => {
      const selected = new Set(s.selected)
      const sub = { ...s.sub }
      if (sourceId) {
        selected.add(sourceId)
        const d = defaultSub(sourceId)
        if (d && !sub[sourceId]) sub[sourceId] = d
      }
      return { ...s, selected, sub, view: 'form' }
    })

  const goStep3 = () => {
    if (!planClean) return
    const ids = orderedSelected(state.selected)
    setState((s) => ({
      ...s,
      step: 3,
      view: 'form',
      prep: ensurePrep(s.prep, ids, s.sub),
      openPrep: ids[0] ?? null,
      modStage: s.flow === 'modify' ? 'collect' : null
    }))
  }

  const collectBack = () =>
    setState((s) =>
      s.flow === 'modify' ? { ...s, step: 2, modStage: 'pick' } : { ...s, step: 2, view: 'form' }
    )

  const updatePrep = (id: SourceId, patch: Partial<PrepValues>) => {
    setState((s) => {
      const cur = s.prep[id] ?? emptyPrep(id, s.sub[id] ?? defaultSub(id))
      const optionalSources = new Set(s.optionalSources)
      const isCompanionMapping =
        (s.modAction === 'add' && s.modResource === 'plc' && id === 'ipmap') ||
        (s.modAction === 'add' && s.modResource === 'robotics' && id === 'roboticsmap') ||
        (s.modAction === 'add' && s.modResource === 'vision' && id === 'visionmap')
      if (isCompanionMapping && ('file' in patch || 'files' in patch)) {
        const hasFile = !!patch.file || (patch.files?.length ?? 0) > 0
        if (hasFile) optionalSources.delete(id)
        else optionalSources.add(id)
      }
      return {
        ...s,
        optionalSources,
        prep: {
          ...s.prep,
          [id]: {
            ...cur,
            ...patch,
            ...('file' in patch || 'files' in patch ? { stagedFiles: [], missingFiles: [] } : {}),
            fields: { ...cur.fields, ...(patch.fields ?? {}) },
            checks: { ...cur.checks, ...(patch.checks ?? {}) }
          }
        }
      }
    })
    if ('file' in patch || 'files' in patch) {
      const draftId = getState().draftId
      if (draftId) {
        void stageIngestWizardSourceFiles(draftId, id).catch(() => {
          // Start ingest retries staging and surfaces any durable upload error.
        })
      }
    }
  }

  const setOpenPrep = (id: SourceId | null) => setState((s) => ({ ...s, openPrep: id }))

  const collectNext = () => {
    if (!prepReady) return
    const launchQueue = orderedSelected(state.selected).filter(
      (id) => LAUNCHABLE[id] && !isOptionalPrepSkipped(state, id) && isSourcePrepReady(state, id)
    )
    setState((s) => ({
      ...s,
      step: 4,
      launchQueue,
      activeLaunchId: null,
      launched: new Set(),
      queueStarted: false,
      run: { ...INITIAL.run }
    }))
  }

  const editRunInputs = () =>
    setState((s) => ({
      ...s,
      step: 3,
      view: 'form',
      openPrep: s.run.pausedSourceId,
      run: { ...s.run, editing: true }
    }))

  const returnToRun = () => {
    if (!prepReady) return
    const launchQueue = orderedSelected(state.selected).filter(
      (id) => LAUNCHABLE[id] && !isOptionalPrepSkipped(state, id) && isSourcePrepReady(state, id)
    )
    setState((s) => {
      const nextPending = s.queueStarted
        ? ((s.run.pausedSourceId && launchQueue.includes(s.run.pausedSourceId)
            ? s.run.pausedSourceId
            : launchQueue.find(
                (id) =>
                  !s.launched.has(id) &&
                  s.run.statuses[id] !== 'done' &&
                  s.run.statuses[id] !== 'skipped'
              )) ?? null)
        : null
      return {
        ...s,
        step: 4,
        launchQueue,
        activeLaunchId: null,
        run: {
          ...s.run,
          editing: false,
          paused: nextPending !== null,
          pausedSourceId: nextPending,
          pauseReason: nextPending ? 'error' : null
        }
      }
    })
  }

  // ── Modify flow ──────────────────────────────────────────────────────────

  const pickModResource = (id: ModifyResourceId) =>
    setState((s) =>
      s.launched.size > 0 && s.modResource !== id
        ? s
        : {
            ...s,
            modResource: id,
            modAction: defaultModifyAction(id),
            optionalSources: new Set(),
            modTargetId: null,
            modAck: false
          }
    )

  const pickModAction = (action: ModifyAction) =>
    setState((s) => {
      if (s.launched.size > 0 && s.modAction !== action) return s
      if (s.modResource && !isModifyActionAvailable(s.modResource, action)) return s
      return { ...s, modAction: action, modTargetId: null, modAck: false }
    })

  const goModifyDetail = () => {
    const { modResource, modAction } = state
    if (!modResource || !modAction) return
    if (!isModifyActionAvailable(modResource, modAction)) return

    if (modAction === 'add') {
      const ids = ADD_SOURCES[modResource] ?? []
      const selected = new Set(ids)
      const optionalSources = new Set<SourceId>()
      if (modResource === 'plc') optionalSources.add('ipmap')
      if (modResource === 'robotics') optionalSources.add('roboticsmap')
      if (modResource === 'vision') optionalSources.add('visionmap')
      const sub: WizardState['sub'] = {}
      for (const id of ids) {
        const d = defaultSub(id)
        if (d) sub[id] = d
      }
      setState((s) => ({
        ...s,
        step: 3,
        selected,
        sub,
        prep: ensurePrep(s.prep, ids, sub),
        optionalSources,
        openPrep: ids[0] ?? null,
        modStage: 'collect'
      }))
      return
    }

    if (modResource === 'docs' && modAction === 'update') {
      setState((s) => ({ ...s, step: 3, modStage: 'docmap' }))
      return
    }

    if (modAction === 'update' && modResource === 'plc') {
      // No-upload pull-sync target picker (StepUpdatePlc) — fetches eligible
      // controllers and launches one `plc_update` job per selection.
      setState((s) => ({ ...s, step: 3, modStage: 'plcupdate' }))
      return
    }

    if (modAction === 'update' && modResource === 'pss') {
      setState((s) => ({ ...s, step: 3, modStage: 'pssupdate' }))
      return
    }

    if (modAction === 'update' && (modResource === 'scada' || modResource === 'ipmap')) {
      const ids = UPDATE_SOURCES[modResource] ?? []
      const selected = new Set(ids)
      const sub: WizardState['sub'] = {}
      for (const id of ids) {
        const d = defaultSub(id)
        if (d) sub[id] = d
      }
      setState((s) => ({
        ...s,
        step: 3,
        selected,
        sub,
        prep: ensurePrep(s.prep, ids, sub),
        openPrep: ids[0] ?? null,
        modStage: 'collect'
      }))
      return
    }

    if (modAction === 'update') {
      setState((s) => ({ ...s, step: 3, modStage: 'pickupdate' }))
      return
    }

    if (modAction === 'remove') {
      setState((s) => ({ ...s, step: 3, modStage: 'remove', modAck: false }))
    }
  }

  const backToPick = () => setState((s) => ({ ...s, step: 2, modStage: 'pick' }))

  /**
   * Enter Step 04 with a single `plcupdate` source whose prep carries the
   * operator's selected controllers. The headless launch queue then fans the
   * selection out into one `plc_update` ingest job per controller.
   */
  const startPlcUpdate = (items: LaunchPlcUpdateItem[]) =>
    setState((s) => ({
      ...s,
      step: 4,
      selected: new Set<SourceId>(['plcupdate']),
      prep: {
        ...s.prep,
        plcupdate: {
          ...emptyPrep('plcupdate'),
          fields: { selectedItems: JSON.stringify(items) }
        }
      },
      launchQueue: ['plcupdate'],
      activeLaunchId: null,
      launched: new Set(),
      queueStarted: false
    }))

  /**
   * Enter Step 04 with a single `pssupdate` source. A journal backfill
   * carries its fixed window; a PssTag re-sync carries only the mirror and
   * is launched through `/api/ontology/ingest/pss/sync-tags`.
   */
  const startPssUpdate = (input: PssUpdateInput) => {
    const fields: Record<string, string> =
      input.action === 'sync_tags'
        ? {
            pssAction: 'sync_tags',
            mirrorId: input.mirrorId,
            connectionName: input.connectionName
          }
        : {
            mirrorId: input.mirrorId,
            connectionName: input.connectionName,
            windowStart: input.windowStart,
            windowEnd: input.windowEnd
          }
    setState((s) => ({
      ...s,
      step: 4,
      selected: new Set<SourceId>(['pssupdate']),
      prep: {
        ...s.prep,
        pssupdate: { ...emptyPrep('pssupdate'), fields }
      },
      launchQueue: ['pssupdate'],
      activeLaunchId: null,
      launched: new Set(),
      queueStarted: false
    }))
  }

  const setModTarget = (id: string) => setState((s) => ({ ...s, modTargetId: id }))
  const toggleModAck = () => setState((s) => ({ ...s, modAck: !s.modAck }))

  const goModifyRun = () =>
    setState((s) => {
      const launchQueue = orderedSelected(s.selected).filter(
        (id) => LAUNCHABLE[id] && !isOptionalPrepSkipped(s, id) && isSourcePrepReady(s, id)
      )
      return {
        ...s,
        step: 4,
        launchQueue,
        activeLaunchId: null,
        launched: new Set(),
        queueStarted: false,
        run: { ...INITIAL.run }
      }
    })

  // ── Headless launch queue (driven by useHeadlessLaunch) ──────────────────

  const beginQueue = () => {
    if (state.launchQueue.length === 0) return
    setState((s) => ({
      ...s,
      queueStarted: true,
      launched: new Set(),
      activeLaunchId: null
    }))
  }

  const setActiveLaunchId = (id: SourceId | null) =>
    setState((s) => ({ ...s, activeLaunchId: id, queueStarted: true }))

  const markLaunched = (id: SourceId) =>
    setState((s) => {
      const launched = new Set(s.launched)
      launched.add(id)
      return { ...s, launched }
    })

  const clearActiveLaunch = () => setState((s) => ({ ...s, activeLaunchId: null }))

  const updateRun = React.useCallback(
    (update: Partial<WizardRunState> | ((run: WizardRunState) => WizardRunState)) =>
      setState((s) => ({
        ...s,
        run: typeof update === 'function' ? update(s.run) : { ...s.run, ...update }
      })),
    [setState]
  )

  return {
    state,
    drafts,
    siteName,
    step1Valid,
    step1Saving,
    runNameError,
    issues,
    planClean,
    prepReady,
    startWizard,
    startWizardWithSource,
    goList,
    resumeDraft,
    discardDraft,
    getState,
    hasDraft,
    setMode,
    setRunName,
    setNewSiteName,
    pickExistingSite,
    goStep2,
    backTo1,
    toggleSource,
    setSub,
    checkPlan,
    backTo2,
    waiveIssue,
    fixIssue,
    goStep3,
    collectBack,
    updatePrep,
    setOpenPrep,
    collectNext,
    editRunInputs,
    returnToRun,
    pickModResource,
    pickModAction,
    goModifyDetail,
    backToPick,
    startPlcUpdate,
    startPssUpdate,
    setModTarget,
    toggleModAck,
    goModifyRun,
    beginQueue,
    setActiveLaunchId,
    markLaunched,
    clearActiveLaunch,
    updateRun
  }
}

export type IngestWizardApi = ReturnType<typeof useIngestWizard>
